import { c as _c } from "react/compiler-runtime";
import { Badge, Divider } from '@onefootprint/ui';
import { useState } from 'react';
import { AddSectionButton, AddSectionOverlay, toneStyles } from '@/components/block-primitives';
import type { DecisionBlockConfig } from './artifact-block-types';
import EditableText from './editable-text';
type ArtifactDecisionBlockProps = {
  config: DecisionBlockConfig;
  editable?: boolean;
  onChange?: (next: DecisionBlockConfig) => void;
};
const ArtifactDecisionBlock = t0 => {
  const $ = _c(51);
  const {
    config,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [addRationale, setAddRationale] = useState(false);
  const [addNote, setAddNote] = useState(false);
  const styles = toneStyles[config.severity];
  let t2;
  if ($[0] !== config || $[1] !== onChange) {
    t2 = next => onChange?.({
      ...config,
      ...next
    });
    $[0] = config;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patch = t2;
  const showHeadline = editable || !!config.headline;
  const showRationale = !!config.rationale || addRationale;
  const showNote = !!config.note || addNote;
  let t3;
  if ($[3] !== editable || $[4] !== showNote || $[5] !== showRationale) {
    t3 = editable && (!showRationale || !showNote) && <AddSectionOverlay position="bottom">{!showRationale && <AddSectionButton label="Add a rationale" onClick={() => setAddRationale(true)} />}{!showNote && <AddSectionButton label="Add a note" onClick={() => setAddNote(true)} />}</AddSectionOverlay>;
    $[3] = editable;
    $[4] = showNote;
    $[5] = showRationale;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = config.title ?? "Investigation decision";
  let t5;
  if ($[7] !== patch) {
    t5 = title => patch({
      title
    });
    $[7] = patch;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = config.title ?? "Investigation decision";
  let t7;
  if ($[9] !== editable || $[10] !== t5 || $[11] !== t6) {
    t7 = <EditableText as="div" className="min-w-0 truncate text-body-3 text-tertiary" editable={editable} label="Decision title" onSubmit={t5} value={t6} />;
    $[9] = editable;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== patch) {
    t8 = outcome => patch({
      outcome
    });
    $[13] = patch;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== config.outcome || $[16] !== editable || $[17] !== t8) {
    t9 = <EditableText editable={editable} label="Outcome" onSubmit={t8} value={config.outcome} />;
    $[15] = config.outcome;
    $[16] = editable;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== styles.badgeVariant || $[20] !== t9) {
    t10 = <Badge className="shrink-0" variant={styles.badgeVariant}>{t9}</Badge>;
    $[19] = styles.badgeVariant;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t7) {
    t11 = <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3">{t7}{t10}</div>;
    $[22] = t10;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== showHeadline || $[26] !== showRationale) {
    t12 = (showHeadline || showRationale) && <div className="px-4 md:px-6"><Divider /></div>;
    $[25] = showHeadline;
    $[26] = showRationale;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== addRationale || $[29] !== config.headline || $[30] !== config.rationale || $[31] !== editable || $[32] !== patch || $[33] !== showHeadline || $[34] !== showRationale) {
    t13 = (showHeadline || showRationale) && <div className="flex flex-col gap-5 px-4 py-4 md:px-6 md:py-5">{showHeadline && <EditableText as="h2" className="max-w-[60ch] text-heading-3 text-primary" editable={editable} label="Decision headline" onSubmit={headline => patch({
        headline
      })} optional={true} placeholder="Add a headline" value={config.headline ?? ""} />}{showRationale && <EditableText as="div" autoFocus={addRationale} block={true} className="text-body-3 leading-5 text-secondary" editable={editable} label="Rationale" multiline={true} onSubmit={rationale => patch({
        rationale
      })} onEmptyBlur={() => setAddRationale(false)} optional={true} placeholder="Add a rationale" value={config.rationale ?? ""} />}</div>;
    $[28] = addRationale;
    $[29] = config.headline;
    $[30] = config.rationale;
    $[31] = editable;
    $[32] = patch;
    $[33] = showHeadline;
    $[34] = showRationale;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== addNote || $[37] !== config.note || $[38] !== editable || $[39] !== patch || $[40] !== showNote) {
    t14 = showNote && <div className="border-t border-tertiary bg-secondary px-4 md:px-6 py-3 text-caption-4 text-tertiary"><EditableText autoFocus={addNote} block={true} editable={editable} label="Decision note" onSubmit={note => patch({
        note
      })} onEmptyBlur={() => setAddNote(false)} optional={true} placeholder="Add a note" value={config.note ?? ""} /></div>;
    $[36] = addNote;
    $[37] = config.note;
    $[38] = editable;
    $[39] = patch;
    $[40] = showNote;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t4) {
    t15 = <section aria-label={t4} className="overflow-hidden rounded-lg border border-tertiary bg-primary">{t11}{t12}{t13}{t14}</section>;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t4;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t15 || $[49] !== t3) {
    t16 = <div className="group relative">{t3}{t15}</div>;
    $[48] = t15;
    $[49] = t3;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  return t16;
};
export default ArtifactDecisionBlock;